import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { cloneDeep } from 'lodash-es';

import { API_URL } from '@constants';
import { GroupSubType } from '../models/common';
import { Group, GroupCreateInput } from '../models/group';
import { Membership } from '../models/membership';
import { ModalService } from './modal.service';

@Service()
export class GroupService {
  modalService = inject(ModalService);
  http = inject(HttpClient);

  getGroups = () => this.http.get<Group[]>(`${API_URL}/groups`);

  getGroup = (rid: number) => this.http.get<Group>(`${API_URL}/groups/${rid}`);

  updateGroup = (groupId: number, groupData: Partial<Group>) =>
    this.http.put<Group>(`${API_URL}/groups/${groupId}`, groupData);

  getGroupConfig = (groupId: number) =>
    this.http.get<Record<string, unknown>>(
      `${API_URL}/groups/${groupId}/config`
    );

  createGroup = (groupData: GroupCreateInput) =>
    this.http.post<Group>(`${API_URL}/groups`, groupData);

  deleteMemberships = async (group: Group, users: Membership[]) => {
    const { DeleteGroupMembershipsModalComponent } =
      await import('../../modules/account/components/delete-group-memberships-modal.component');

    return this.modalService.open(
      DeleteGroupMembershipsModalComponent,
      {
        group: cloneDeep(group),
        users: cloneDeep(users),
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  addMemberships = async (group: Group) => {
    const { AddGroupMembershipsModalComponent } =
      await import('../../modules/account/components/add-group-memberships-modal.component');

    return this.modalService.open(
      AddGroupMembershipsModalComponent,
      {
        group: cloneDeep(group),
      },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  editNotifications = async (group: Group) => {
    const { EditGroupNotificationsModalComponent } =
      await import('../../modules/account/components/edit-group-notifications-modal.component');

    return this.modalService.open(EditGroupNotificationsModalComponent, {
      group: cloneDeep(group),
    });
  };

  edit = async (group: Group) => {
    const { EditGroupModalComponent } =
      await import('../../modules/account/components/edit-group-modal.component');

    return this.modalService.open(
      EditGroupModalComponent,
      { group: cloneDeep(group) },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  create = async (subtype: GroupSubType) => {
    const { CreateGroupModalComponent } =
      await import('../../modules/account/components/new-group-modal.component');

    return this.modalService.open(
      CreateGroupModalComponent,
      { subtype },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };
}
